"use client";

import { DownOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Input as AntInput,
  Button,
  DatePicker,
  Dropdown,
  Flex,
  Modal,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import dayjs from "dayjs";
import { EditorState } from "lexical";
import React, { useEffect, useState } from "react";
import { useToken } from "../../hooks/useToken";
import { TInterest } from "../../models/interests.model";
import { TMedia } from "../../models/media.model";
import { EPostStatus, EPostType, TPost } from "../../models/posts.model";
import { TConfig, TResponse, getConfig } from "../../services/apiWorker";
import { getInterests } from "../../services/interests.service";
import {
  deleteMedia,
  getMedia,
  getMediaFile,
  uploadMedia,
} from "../../services/media.service";
import { updatePost } from "../../services/posts.service";
import { hexToRgba } from "../../utils/hexToRgba";
import { EditorTeste } from "../editor/EditorTeste";
import { CategoryDropdownContent } from "./CategoryDropDown";

const { Text, Title } = Typography;

export function ModalPostEdit({
  post,
  onUpdate,
  onClose,
}: {
  post: TPost;
  onUpdate: (data: TPost) => void;
  onClose: () => void;
}) {
  const { token } = useToken();
  const [isOpen, setOpen] = useState<boolean>(true);
  const [interests, setInterests] = useState<TResponse<TInterest>>(TResponse);
  const [selectedInterests, setSelectedInterests] = useState<string[]>(
    post.interests || []
  );
  const [date, setDate] = useState<string>(
    new Date(post.eventDate || post.createdAt).toISOString()
  );
  const [title, setTitle] = useState<string>(post.title);
  const [location, setLocation] = useState<string>(
    post.metadata.location || ""
  );
  const [files, setFiles] = useState<TMedia[]>([]);
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<TMedia>();

  const [state, setState] = useState<EditorState | string | undefined>(
    post?.content?.text ||
      '{"root":{"children":[{"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}'
  );

  const [isUpdatingPost, setIsUpdatingPost] = useState<boolean>(false);
  const [config, setConfig] = useState<TConfig>();
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [isLoadingMedia, setIsLoadingMedia] = useState(true);
  const [isLoadingInterests, setIsLoadingInterests] = useState(true);

  const renderCustomTag = (
    interest: TInterest,
    closable = false,
    onClose?: () => void
  ) => {
    const tagColor = interest?.color || token.colorPrimary;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        key={interest.id}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{
          border: `1px solid ${tagColor}`,
          color: tagColor,
          backgroundColor: hexToRgba(tagColor, 0.2) || "",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "150px",
          verticalAlign: "middle",
        }}
      >
        {" "}
        {interest.name}{" "}
      </Tag>
    );
  };
  const handleAddInterestToList = (newInterest: TInterest) => {
    setInterests((prevInterests) => {
      const currentContent = prevInterests?.content || [];
      if (currentContent.some((i) => i.id === newInterest.id)) {
        return prevInterests;
      }
      return { ...prevInterests, content: [...currentContent, newInterest] };
    });
  };
  const renderCategoryDropdown = () => (
    <div
      style={{
        width: "300px",
        backgroundColor: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary,
      }}
    >
      {" "}
      <CategoryDropdownContent
        allCategories={interests?.content || []}
        selectedCategories={selectedInterests}
        onChange={setSelectedInterests}
        onAddCategory={handleAddInterestToList}
      />{" "}
    </div>
  );

  useEffect(() => {
    const loadConfig = async () => {
      setIsLoadingConfig(true);
      try {
        const configData = await getConfig();
        setConfig(configData);
      } catch (error) {
        console.error("Failed to load config:", error);
        message.error("Failed to load configuration.");
      } finally {
        setIsLoadingConfig(false);
      }
    };
    loadConfig();
  }, []);
  useEffect(() => {
    if (!post.medias || post.medias.length === 0) {
      setIsLoadingMedia(false);
      setFiles([]);
      setUploadFileList([]);
      return;
    }
    setIsLoadingMedia(true);
    const mediaPromises = post.medias.map(
      (mediaId) =>
        new Promise((resolve) => {
          getMedia({
            mediaId: mediaId,
            onSuccess: (data) => {
              getMediaFile({
                mediaId: data.id,
                onSuccess: (mediaFile) => {
                  resolve({
                    ...data,
                    thumbUrl: URL.createObjectURL(new Blob([mediaFile])),
                  });
                },
                onError: (error) => {
                  console.error(
                    `Failed to fetch media file ${mediaId}:`,
                    error
                  );
                  resolve(null);
                },
              });
            },
            onError: (error) => {
              console.error(`Failed to fetch media ${mediaId}:`, error);
              resolve(null);
            },
          });
        })
    );
    Promise.all(mediaPromises)
      .then((results) => {
        const loadedFiles = results.filter(
          (media): media is TMedia => media !== null
        );
        setFiles(loadedFiles);
        const initialUploadFileList = loadedFiles.map(
          (media): UploadFile => ({
            uid: media.id,
            name: media.name || `media_${media.id}`,
            status: "done",
            url: media.href,
            response: media,
            thumbUrl: (media as any).thumbUrl,
          })
        );
        setUploadFileList(initialUploadFileList);
      })
      .catch((error) => {
        console.error("Error fetching media details:", error);
        message.error("Error loading existing media files.");
      })
      .finally(() => {
        setIsLoadingMedia(false);
      });
  }, [post.medias]);
  useEffect(() => {
    setIsLoadingInterests(true);
    getInterests({
      onSuccess: (data) => {
        setInterests({ ...data, content: data.content || [] });
        setIsLoadingInterests(false);
      },
      onError: (error) => {
        console.error("Failed to fetch interests:", error);
        message.error("Failed to load categories.");
        setIsLoadingInterests(false);
      },
    });
  }, []);

  function handleClose() {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 100);
  }
  function onConfirm() {
    if (
      isUpdatingPost ||
      isLoadingConfig ||
      isLoadingMedia ||
      isLoadingInterests
    )
      return;
    let contentText = post.content.text || JSON.stringify({});
    try {
      if (state && typeof state !== "string") {
        contentText = JSON.stringify(state.toJSON());
      } else if (typeof state === "string") {
        try {
          JSON.parse(state);
          contentText = state;
        } catch (parseError) {
          console.warn(
            "Editor state string is not valid JSON...",
            state,
            parseError
          );
          contentText = post.content.text || JSON.stringify({});
        }
      }
    } catch (e) {
      console.error("Error processing editor state...", e);
      contentText = post.content.text || JSON.stringify({});
    }
    setIsUpdatingPost(true);
    updatePost({
      postId: post.id,
      content: { text: contentText },
      interests: selectedInterests,
      medias: files.map((media) => media.id),
      status: EPostStatus.PUBLISH,
      title,
      type: EPostType.EVENT,
      eventDate: new Date(date || new Date().toISOString()),
      metadata: { location },
      onSuccess: (data) => {
        onUpdate(data);
        handleClose();
        message.success("Event updated successfully!");
      },
      onError: (data) => {
        const errorMsg = Array.isArray(data?.message)
          ? data.message.join(", ")
          : data?.message || "Failed to update event.";
        message.error(errorMsg);
        console.error("Update Post Error:", data);
        setIsUpdatingPost(false);
      },
    });
  }

  const handlePreview = async (file: UploadFile) => {
    const mediaData = file.response as TMedia;
    if (mediaData && mediaData.id) {
      setPreviewImage(mediaData);
      setPreviewVisible(true);
    } else if (file.url || file.preview) {
      setPreviewImage({
        id: file.uid,
        href: file.url || file.preview || "",
        name: file.name,
      } as TMedia);
      setPreviewVisible(true);
    } else {
      const media = files.find((f) => f.id === file.uid);
      if (media) {
        setPreviewImage(media);
        setPreviewVisible(true);
      } else {
        message.warning("Could not get preview details.");
        console.warn("Preview failed for file:", file);
      }
    }
  };
  const handleUploadChange: UploadProps["onChange"] = (info) => {
    const { file, fileList: newFileList } = info;
    const status = file.status;
    const updatedFileList = newFileList.map((f) => {
      if (f.status === "done" && f.response && (f.response as TMedia).id) {
        const mediaData = f.response as TMedia;
        return {
          ...f,
          uid: mediaData.id,
          url: mediaData.href,
          name: mediaData.name || f.name,
          response: mediaData,
        };
      }
      return f;
    });
    setUploadFileList(updatedFileList);
    if (status === "done") {
      const responseMedia = file.response as TMedia;
      if (responseMedia && responseMedia.id) {
        message.success(`${file.name} file uploaded successfully`);
        setFiles((currentFiles) => {
          const fileExists = currentFiles.some(
            (f) => f.id === responseMedia.id
          );
          const newFiles = !fileExists
            ? [...currentFiles, responseMedia]
            : currentFiles.map((f) =>
                f.id === responseMedia.id ? responseMedia : f
              );
          return newFiles;
        });
      } else {
        message.error(`Error processing server response for ${file.name}.`);
        setUploadFileList((prevList) =>
          prevList.filter((item) => item.uid !== file.uid)
        );
      }
    } else if (status === "error") {
      message.error(`${file.name} file upload failed.`);
      setUploadFileList((prevList) =>
        prevList.filter((item) => item.uid !== file.uid)
      );
    } else if (status === "removed") {
      const mediaIdToRemove = (file.response as TMedia)?.id || file.uid;
      setFiles((currentFiles) =>
        currentFiles.filter((f) => f.id !== mediaIdToRemove)
      );
    }
  };
  const handleUploadRemove: UploadProps["onRemove"] = (file) => {
    const mediaIdToRemove = (file.response as TMedia)?.id || file.uid;
    if (!mediaIdToRemove) {
      setFiles((prevFiles) => prevFiles.filter((f) => f.id !== file.uid));
      setUploadFileList((prevUploadList) =>
        prevUploadList.filter((f) => f.uid !== file.uid)
      );
      return true;
    }
    const fileIndex = uploadFileList.findIndex(
      (f) => f.uid === mediaIdToRemove
    );
    setUploadFileList((prevUploadList) =>
      prevUploadList.filter((f) => f.uid !== mediaIdToRemove)
    );
    setFiles((prevFiles) => prevFiles.filter((f) => f.id !== mediaIdToRemove));
    deleteMedia({
      mediaId: mediaIdToRemove,
      onSuccess: () => {
        message.success(`Media "${file.name}" removed.`);
      },
      onError: (err) => {
        message.error(`Failed to remove media "${file.name}" from server.`);
        if (fileIndex > -1) {
          setUploadFileList((prevUploadList) => {
            const newList = [...prevUploadList];
            newList.splice(fileIndex, 0, file);
            return newList;
          });
          const originalMedia =
            (file.response as TMedia) ||
            files.find((f) => f.id === mediaIdToRemove);
          if (originalMedia) {
            setFiles((prevFiles) => [...prevFiles, originalMedia]);
          }
        }
      },
    });
    return false;
  };
  const handleCustomRequest: UploadProps["customRequest"] = (options) => {
    uploadMedia({
      file: options.file as File,
      onSuccess: (data) => {
        getMedia({
          mediaId: data.id,
          onSuccess: (mediaData) => {
            options.onSuccess?.(
              mediaData,
              options.file as unknown as XMLHttpRequest
            );
          },
          onError: (fetchError) => {
            options.onError?.(
              new Error(`Failed to fetch media details: ${fetchError.message}`),
              { id: data.id }
            );
          },
        });
      },
      onError: (error) => {
        message.error(error.message || "Upload failed");
        options.onError?.(error);
      },
    });
  };
  const commonUploadProps: Omit<
    UploadProps,
    "listType" | "showUploadList" | "children"
  > = {
    accept: "image/*,video/*,audio/*",
    multiple: true,
    fileList: uploadFileList,
    disabled: isLoadingConfig || !config || isLoadingMedia,
    customRequest: handleCustomRequest,
    onPreview: handlePreview,
    onRemove: handleUploadRemove,
    onChange: handleUploadChange,
  };

  const customModalTitle = (
    <Flex
      align="center"
      justify="space-between"
      style={{
        width: "100%",
        paddingLeft: token.paddingLG,
        paddingRight: token.paddingLG,
      }}
    >
      <Title level={5} style={{ margin: 0 }}>
        Edit event
      </Title>
      <Flex gap={token.marginSM}>
        <Button onClick={handleClose} type="default">
          {" "}
          Cancel{" "}
        </Button>
        <Button
          loading={isUpdatingPost}
          onClick={onConfirm}
          type="primary"
          disabled={isLoadingConfig || isLoadingMedia || isLoadingInterests}
        >
          Save
        </Button>
      </Flex>
    </Flex>
  );

  return (
    <>
      <Modal
        title={customModalTitle}
        open={isOpen}
        onCancel={handleClose}
        footer={null}
        closable={false}
        width={1100}
        centered
        style={{ maxWidth: "95%", padding: 0 }}
        styles={{
          content: {
            backgroundColor: `${token.colorBgBase}E0`,
            backdropFilter: "blur(20px)",
            borderRadius: token.borderRadiusLG,
            overflow: "hidden",
            padding: 0,
          },
          header: {
            backgroundColor: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: `${token.padding}px 0`,
            margin: 0,
          },
          body: {
            overflowY: "auto",
            maxHeight: "calc(100vh - 140px)",
            padding: token.paddingLG,
          },
        }}
      >
        <Flex vertical style={{ width: "100%" }}>
          <Text style={{ marginBottom: token.marginXS }}>Event Title</Text>
          <AntInput
            placeholder="Event title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            size="large"
          />
        </Flex>

        <Flex
          style={{ width: "100%", marginTop: token.marginLG }}
          gap={token.margin}
        >
          <Flex vertical style={{ width: "50%" }}>
            <Text style={{ marginBottom: token.marginXS }}>Event Date</Text>
            <DatePicker
              style={{ width: "100%", height: "38px" }}
              value={date ? dayjs(date) : null}
              onChange={(d) => setDate(d ? d.toISOString() : "")}
              format={localStorage.getItem("dateFormat") + " HH:mm"}
              showTime={{ format: "HH:mm" }}
            />
          </Flex>
          <Flex vertical style={{ width: "50%" }}>
            <Text style={{ marginBottom: token.marginXS }}>Location</Text>
            <AntInput
              placeholder="Location"
              style={{ height: "38px" }}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </Flex>
        </Flex>

        <Flex
          style={{ width: "100%", marginTop: token.marginLG }}
          gap={token.margin}
        >
          <Flex vertical style={{ width: "50%" }}>
            <Text style={{ marginBottom: token.marginXS }}>Category</Text>
            <Dropdown
              trigger={["click"]}
              dropdownRender={renderCategoryDropdown}
              disabled={isLoadingInterests || isLoadingMedia || isLoadingConfig}
            >
              <div
                style={{
                  width: "100%",
                  height: "38px",
                  border: `1px solid ${token.colorBorder}`,
                  borderRadius: token.borderRadius,
                  backgroundColor: token.colorBgContainer,
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  overflow: "hidden",
                  padding: `0 ${token.paddingXS}px`,
                  opacity:
                    isLoadingInterests || isLoadingMedia || isLoadingConfig
                      ? 0.5
                      : 1,
                  pointerEvents:
                    isLoadingInterests || isLoadingMedia || isLoadingConfig
                      ? "none"
                      : "auto",
                }}
                tabIndex={0}
                aria-disabled={
                  isLoadingInterests || isLoadingMedia || isLoadingConfig
                }
              >
                <Flex
                  style={{
                    flexGrow: 1,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                  gap={token.paddingXXS}
                >
                  {selectedInterests.length === 0 && !isLoadingInterests && (
                    <Text type="secondary">Select categories</Text>
                  )}
                  {isLoadingInterests && (
                    <Text type="secondary">Loading categories...</Text>
                  )}
                  {selectedInterests.map((id) => {
                    const interest = interests?.content?.find(
                      (i) => i.id === id
                    );
                    return interest ? renderCustomTag(interest, false) : null;
                  })}
                </Flex>
                <DownOutlined
                  style={{
                    color: token.colorTextDescription,
                    marginLeft: token.paddingXS,
                    flexShrink: 0,
                  }}
                />
              </div>
            </Dropdown>
          </Flex>

          <Flex vertical style={{ width: "50%" }}>
            <Text style={{ marginBottom: token.marginXS }}>Upload Photos</Text>
            <Upload
              style={{ width: "100%" }}
              {...commonUploadProps}
              showUploadList={false}
            >
              <div style={{ width: "100%" }}>
                <Button
                  icon={<UploadOutlined />}
                  style={{ width: "100%", height: "38px" }}
                  disabled={commonUploadProps.disabled}
                >
                  Click to Upload
                </Button>
              </div>
            </Upload>
          </Flex>
        </Flex>

        {!isLoadingMedia && (
          <Flex vertical style={{ width: "100%", marginTop: token.marginLG }}>
            {uploadFileList.length > 0 && (
              <Text style={{ marginBottom: token.marginXS }}>Media</Text>
            )}
            <Upload {...commonUploadProps} listType="picture-card"></Upload>
          </Flex>
        )}
        {isLoadingMedia && (
          <Flex
            vertical
            style={{
              width: "100%",
              marginTop: token.marginLG,
              alignItems: "center",
              padding: token.paddingLG,
            }}
          >
            <Text type="secondary">Loading existing media...</Text>
          </Flex>
        )}

        <Flex
          vertical
          style={{
            width: "100%",
            marginTop: token.marginLG,
            marginBottom: token.marginLG,
          }}
        >
          <Text style={{ marginBottom: token.marginXS }}>Description</Text>
          <EditorTeste
            state={state}
            onState={setState}
            styles={{
              editorContainer: {
                minHeight: "150px",
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
              },
              editorInner: {
                background: token.colorBgContainer,
                padding: `${token.paddingSM}px`,
              },
            }}
          />
          {!state && <div>Loading editor...</div>}
        </Flex>
      </Modal>
    </>
  );
}
